import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Stack,
  Text,
  Badge,
  Group,
  Button,
  Divider,
  Anchor,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconUser,
  IconStethoscope,
  IconBuilding,
} from "@tabler/icons-react";
import CaseStatusBadge from "../cases/CaseStatusBadge";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

interface GrossingCaseSummaryProps {
  caseId?: string;
  caseNumber?: string;
  externalCaseNumber?: string | null;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    mrn?: string;
  };
  procedure?: {
    name?: string;
  };
  currentStatus?: {
    name?: string;
  };
  isBlocked?: boolean;
  isRush?: boolean;
  isHighPriority?: boolean;
  accessioningType?: string;
  pathologist?: {
    fullName?: string;
  };
  facility?: {
    name?: string;
  };
  specimenCount?: number;
  blocksCount?: number;
  slidesCount?: number;
  // Navigation
  hasPreviousCase?: boolean;
  hasNextCase?: boolean;
  onPreviousCase?: () => void;
  onNextCase?: () => void;
}

export default function GrossingCaseSummary({
  caseId,
  caseNumber,
  externalCaseNumber,
  patient,
  procedure,
  currentStatus,
  isBlocked = false,
  isRush = false,
  isHighPriority = false,
  accessioningType,
  pathologist,
  facility,
  specimenCount = 0,
  blocksCount,
  slidesCount,
  hasPreviousCase = false,
  hasNextCase = false,
  onPreviousCase,
  onNextCase,
}: GrossingCaseSummaryProps) {
  const navigate = useNavigate();

  const handleCaseNumberClick = useCallback(() => {
    if (caseId) {
      navigate(`/cases/${caseId}`);
    }
  }, [caseId, navigate]);

  const formatDate = useCallback((dateStr?: string) => {
    const dateOnly = parseDateOnly(dateStr);
    return dateOnly ? formatDateOnlyForDisplay(dateOnly) : null;
  }, []);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.firstName, patient?.lastName]
  );

  const handlePreviousCase = useCallback(() => {
    if (hasPreviousCase) onPreviousCase?.();
  }, [hasPreviousCase, onPreviousCase]);

  const handleNextCase = useCallback(() => {
    if (hasNextCase) onNextCase?.();
  }, [hasNextCase, onNextCase]);

  return (
    <Paper p="sm" withBorder>
      <Stack gap="sm">
        {/* Case Number - Clickable link to case details */}
        <Anchor
          size="lg"
          fw={600}
          onClick={handleCaseNumberClick}
          style={{ cursor: caseId ? "pointer" : "default" }}
        >
          {caseNumber || "Loading..."}
        </Anchor>

        {externalCaseNumber && (
          <Badge size="sm" variant="outline" color="gray">
            {externalCaseNumber}
          </Badge>
        )}

        {/* Priority Flags - Rush / High Priority */}
        {(isRush || isHighPriority) && (
          <Group gap="xs">
            {isRush && (
              <Badge color="red" variant="filled" size="sm">
                RUSH
              </Badge>
            )}
            {isHighPriority && (
              <Badge color="orange" variant="filled" size="sm">
                HIGH PRIORITY
              </Badge>
            )}
          </Group>
        )}

        {/* Patient: Name • DOB • MRN */}
        <Text size="sm">
          <Text span fw={500}>
            {patientName || "Unknown"}
          </Text>
          {patient?.dateOfBirth && (
            <Text span c="dimmed">
              {" "}
              • {formatDate(patient.dateOfBirth)}
            </Text>
          )}
          {patient?.mrn && (
            <Text span c="dimmed">
              {" "}
              • {patient.mrn}
            </Text>
          )}
        </Text>

        {/* Status */}
        <CaseStatusBadge
          statusName={currentStatus?.name || "Unknown"}
          isBlocked={isBlocked}
        />

        {/* Procedure */}
        {procedure?.name && (
          <Group gap="xs" wrap="nowrap">
            <IconStethoscope size={14} color="var(--mantine-color-gray-5)" />
            <Text size="sm">{procedure.name}</Text>
          </Group>
        )}

        {/* Workflow • Specimens • Blocks • Slides */}
        <Text size="sm" c="dimmed">
          <Text span tt="capitalize">
            {accessioningType || "Global"}
          </Text>
          <Text span>
            {" "}
            • {specimenCount} specimen{specimenCount !== 1 ? "s" : ""}
          </Text>
          {blocksCount !== undefined && (
            <Text span>
              {" "}
              • {blocksCount} block{blocksCount !== 1 ? "s" : ""}
            </Text>
          )}
          {slidesCount !== undefined && (
            <Text span>
              {" "}
              • {slidesCount} slide{slidesCount !== 1 ? "s" : ""}
            </Text>
          )}
        </Text>

        {/* Pathologist */}
        <Group gap="xs" wrap="nowrap">
          <IconUser size={14} color="var(--mantine-color-gray-5)" />
          <Text size="sm" c={pathologist?.fullName ? undefined : "dimmed"}>
            {pathologist?.fullName || "Unassigned"}
          </Text>
        </Group>

        {/* Facility */}
        {facility?.name && (
          <Group gap="xs" wrap="nowrap">
            <IconBuilding size={14} color="var(--mantine-color-gray-5)" />
            <Text size="sm">{facility.name}</Text>
          </Group>
        )}

        {/* Navigation */}
        <Divider />
        <Group grow gap="xs">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconChevronLeft size={14} />}
            onClick={handlePreviousCase}
            style={{ opacity: hasPreviousCase ? 1 : 0.5 }}
          >
            Previous
          </Button>
          <Button
            variant="light"
            size="xs"
            rightSection={<IconChevronRight size={14} />}
            onClick={handleNextCase}
            style={{ opacity: hasNextCase ? 1 : 0.5 }}
          >
            Next
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
